import React, { useState, useRef, useEffect } from 'react';
import './CustomCalendar.css';

const CustomCalendar = ({ value, onChange, placeholder = "дд.мм.гггг" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [currentMonth, setCurrentMonth] = useState(value ? new Date(value) : new Date());
  const [inputValue, setInputValue] = useState('');
  const calendarRef = useRef(null);
  const inputRef = useRef(null);

  const months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  // Получение текущей даты (без времени)
  const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Закрытие календаря при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Инициализация inputValue при монтировании
  useEffect(() => {
    if (selectedDate) {
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const year = selectedDate.getFullYear();
      setInputValue(`${day}.${month}.${year}`);
    } else {
      setInputValue('');
    }
  }, [selectedDate]);
  

  // Маска ввода даты
  const handleInputChange = (e) => {
    let value = e.target.value.replace(/[^\d.]/g, '');
    
    // Ограничиваем длину
    if (value.length > 10) return;
    
    // Автоматически добавляем точки
    if (value.length === 2 && inputValue.length === 1) {
      value += '.';
    } else if (value.length === 5 && inputValue.length === 4) {
      value += '.';
    }
    
    setInputValue(value);
  };

  // Валидация и установка даты при потере фокуса
  const handleInputBlur = (e) => {
    const value = e.target.value.trim();
    
    if (!value) {
      setSelectedDate(null);
      onChange(null);
      return;
    }

    // Проверяем формат DD.MM.YYYY
    const dateRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    const match = value.match(dateRegex);
    
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const year = parseInt(match[3], 10);
      
      const parsedDate = new Date(year, month, day);
      
      // Проверяем валидность даты
      if (
        parsedDate.getDate() === day &&
        parsedDate.getMonth() === month &&
        parsedDate.getFullYear() === year
      ) {
        // Проверяем, что дата не раньше сегодня
        const today = getToday();
        if (parsedDate >= today) {
          setSelectedDate(parsedDate);
          setCurrentMonth(parsedDate);
          onChange(parsedDate);
        } else {
          // Если дата раньше сегодня, сбрасываем на сегодня
          setSelectedDate(today);
          setCurrentMonth(today);
          onChange(today);
          const day = String(today.getDate()).padStart(2, '0');
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const year = today.getFullYear();
          setInputValue(`${day}.${month}.${year}`);
          alert('Дата не может быть раньше текущего дня');
        }
      } else {
        // Невалидная дата
        alert('Введите корректную дату');
        setInputValue('');
        setSelectedDate(null);
        onChange(null);
      }
    } else {
      // Неправильный формат
      alert('Используйте формат: дд.мм.гггг');
      setInputValue('');
      setSelectedDate(null);
      onChange(null);
    }
  };

  // Генерация дней месяца
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startDate = new Date(firstDay);
    const dayOfWeek = startDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(startDate.getDate() + daysToMonday);
    
    const days = [];
    const today = getToday();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === month;
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
      const isDisabled = date < today; // Дата раньше сегодня - недоступна
      
      days.push({
        date,
        day: date.getDate(),
        isCurrentMonth,
        isToday,
        isSelected,
        isDisabled
      });
    }
    
    return days;
  };

  const handleDateSelect = (date) => {
  const today = getToday();
  
  // Проверяем, что выбранная дата не раньше сегодня
  if (date >= today) {
    setSelectedDate(date);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    setInputValue(`${day}.${month}.${year}`);
    
    // НЕМЕДЛЕННО вызываем onChange при выборе даты
    onChange(date);
    
    setIsOpen(false);
  } else {
    alert('Нельзя выбрать дату раньше текущего дня');
  }
};

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const clearDate = () => {
    setSelectedDate(null);
    setInputValue('');
    onChange(null);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const days = getDaysInMonth();

  return (
    <div className="custom-calendar" ref={calendarRef}>
      <div className="calendar-input-container">
        <input
          ref={inputRef}
          type="text"
          className="calendar-input"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          placeholder={placeholder}
        />
        <div className="calendar-input-actions">
         
          <button 
            className="calendar-toggle-btn"
            onClick={() => setIsOpen(!isOpen)}
            type="button"
          >
            <i className={`fas fa-chevron-down ${isOpen ? 'open' : ''}`}></i>
          </button>
        </div>
      </div>
      
      {isOpen && (
        <div className="calendar-dropdown">
          <div className="calendar-header">
            <button className="nav-button" onClick={goToPreviousMonth} type="button">
              <i className="fas fa-chevron-left"></i>
            </button>
            
            <div className="current-month">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            
            <button className="nav-button" onClick={goToNextMonth} type="button">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          <div className="calendar-weekdays">
            {weekDays.map(day => (
              <div key={day} className="calendar-weekday-bot">{day}</div>
            ))}
          </div>

          <div className="calendar-days-grid">
            {days.map((day, index) => (
              <div
                key={index}
                className={`calendar-grid-day ${
                  !day.isCurrentMonth ? 'calendar-other-month' : ''
                } ${day.isToday ? 'calendar-today' : ''} ${
                  day.isSelected ? 'calendar-selected' : ''
                } ${day.isDisabled ? 'calendar-disabled' : ''}`}
                onClick={() => !day.isDisabled && handleDateSelect(day.date)}
                title={day.isDisabled ? "Нельзя выбрать прошедшую дату" : ""}
              >
                {day.day}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomCalendar;